/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { css } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const stickySuggestionsGroupHeadingStyles = css({
	position: 'sticky',
	width: 'inherit',
	top: 0,
	backgroundColor: token('elevation.surface'),
	zIndex: 1,
});

const suggestionsGroupHeadingStyles = css({
	/**
	 * We use '&&' to increase specificity against Confluence's heading styles,
	 * otherwise the styles would be overriden by .wiki-content styles
	 */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&&': {
		boxSizing: 'border-box',
		// use flex to remove the extra space around children
		display: 'flex',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.150'),
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		font: token('font.body.small'),
		fontweight: token('font.weight.bold'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
		color: token('color.text.subtle'),
	},
});

const groupHeadingStyles = css({
	/**
	 * We use '&&' to increase specificity against Confluence's heading styles,
	 * otherwise the styles would be overriden by .wiki-content styles
	 */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&&': {
		boxSizing: 'border-box',
		marginTop: token('space.0'),
		marginRight: token('space.0'),
		marginBottom: token('space.0'),
		marginLeft: token('space.0'),
		display: 'flex',
		paddingTop: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.050'),
		font: token('font.body'),
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		fontWeight: token('font.weight.medium'),
		color: token('color.text.subtlest'),
	},
});

type ListHeadingProps = { heading: string; id: string };

export const ListHeading = ({ id, heading }: ListHeadingProps): JSX.Element => {
	/* Using h3, as the parent element can potentially put in a h2 heading above suggestions
	 * TODO: EDF-2349 - double check in branch deploy when migrating to compiled to see if passing in
	 * styles directly to css prop in h3 (instead of using classname and nested selector) is still overridden
	 * by Confluence styles, or whether compiled migration mitigates that
	 */
	return (
		/* ignore use-heading rule here as h3 style is overrided with Editor custom font token on higher level */
		/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/design-system/use-heading -- Ignored via go/DSP-18766 */
		<h3
			id={id}
			css={[
				fg('ai-smart-button-team-25') ? groupHeadingStyles : suggestionsGroupHeadingStyles,
				stickySuggestionsGroupHeadingStyles,
			]}
			data-testid={id}
		>
			{heading}
		</h3>
	);
};
